import { useCallback } from "react";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import api from "utils/api";
import { localizeFloorPlanLayout } from "../../utils/floorPlanLayout/localizeFloorPlanLayout";
import { floorPlanLayoutKeys } from "./floorPlanLayoutKeys";
import type { FloorPlanLayout } from "../../utils/types";
import type { UseFloorPlanLayoutArgs, UseFloorPlanLayoutResult } from "./types";

export const EMPTY_FLOOR_PLAN_LAYOUT: FloorPlanLayout = {
  zones: [],
  rooms: [],
  walls: [],
  doors: [],
  windows: [],
};

// Long enough to survive a mode switch, short enough to pick up another user's edit.
const LAYOUT_STALE_TIME_MS = 60_000;

export const useFloorPlanLayout = ({
  floorPlanId,
  isEnabled,
}: UseFloorPlanLayoutArgs): UseFloorPlanLayoutResult => {
  const { i18n } = useTranslation();
  const language = i18n.language;

  const selectLocalizedLayout = useCallback(
    (layout: FloorPlanLayout) => localizeFloorPlanLayout({ layout, language }),
    [language],
  );

  const layoutQuery = useQuery({
    queryKey: floorPlanLayoutKeys.detail(floorPlanId),
    queryFn: async (): Promise<FloorPlanLayout> => {
      if (floorPlanId === undefined) return EMPTY_FLOOR_PLAN_LAYOUT;
      const layout: FloorPlanLayout =
        await api.getQuantitySurveyorFloorPlanLayout(floorPlanId);
      return layout;
    },
    select: selectLocalizedLayout,
    enabled: isEnabled && floorPlanId !== undefined,
    staleTime: LAYOUT_STALE_TIME_MS,
  });

  // A cached layout must not leak into planning mode, where nothing is scoped.
  const layout = isEnabled
    ? (layoutQuery.data ?? EMPTY_FLOOR_PLAN_LAYOUT)
    : EMPTY_FLOOR_PLAN_LAYOUT;

  return {
    layout,
    isLoading: layoutQuery.isLoading,
    isError: layoutQuery.isError,
  };
};
